import { takeEvery, all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import apiService from 'app/bff/services/apiService';
import { ReturnCodesEnum } from 'app/bff/enums/codes';
import { InsuranceModesEnum, InsuranceIdEnum } from 'app/bff/enums/insurance';
import { ProductIdEnum } from 'app/bff/enums/product';
import { PolicyRulesEnum } from 'app/bff/enums/policy';
import { BaseResp } from 'app/bff/models/base';
import { ProductPolicyRulevalidateReq, ProductPolicyRulevalidateResp, PolicyApplicantResp, PolicyInsuredResp, PolicyBeneficiaryResp, ErrMsgResp } from 'app/bff/models/product/productPolicyRulevalidate';
import { AaOtpSendcodeResp } from 'app/bff/models/aa/aaOtpSendcode';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { PolicyTravelResp } from 'app/bff/models/policy/policyTravel';
import { PaymentCreditAuthResp } from 'app/bff/models/payment/paymentCreditAuth';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/OnlineInsure/TravelInsurance/types';
import { RootState } from 'app/store/types';
import { sagaBoundary } from 'app/store/service';
import { TravelInsuranceState, INSURE_TRAVEL_INSURANCE__INIT_PROCESS, INSURE_TRAVEL_INSURANCE__VALIDATE_POLICY_RULES, INSURE_TRAVEL_INSURANCE__VALIDATE_POLICY_RULE_LAUNDERING_LIST, INSURE_TRAVEL_INSURANCE__SEND_OTP, INSURE_TRAVEL_INSURANCE__RESEND_OTP, INSURE_TRAVEL_INSURANCE__AUTH_OTP, INSURE_TRAVEL_INSURANCE__SEND_POLICY, INSURE_TRAVEL_INSURANCE__EXECUTE_CREDIT_CARD_PAYMENT, ValidatePolicyRulesAction, ValidatePolicyRuleLaunderingListAction, SendOTPAction, ResendOTPAction, AuthOTPAction, SendPolicyAction, ExecuteCreditCardPaymentAction } from './types';
import { initProcessDoneAction, validatePolicyRuleLaunderingListAction, setPolicyStatusAction, saveInsuranceInfoPageDataAction, setAccessibleStepAction, sendOTPDoneAction, resendOTPDoneAction, sendPolicyAction, sendPolicyDoneAction } from './actions';

/**
 * @description 初始化流程 (初始取得相關資料)
 */
function * initProcess () {
  const response: TravelInsuranceState['static'] = yield all({
    travelRegions: call(apiService.postInfoTravelregionQuery),
    coverages: call(apiService.postProductGetcoverage, { bcType: InsuranceModesEnum.OnlineContractOnly, insuranceId: InsuranceIdEnum.Travel, productId: ProductIdEnum.Travel }),
    questionnaires: call(apiService.postProductGetkycquestion, { productId: ProductIdEnum.Travel, category: 'KYC' })
  });
  yield put(initProcessDoneAction(response));
}

/**
 * @description 執行保單規則檢核
 */
function * validatePolicyRules (action: ValidatePolicyRulesAction) {
  const response: ProductPolicyRulevalidateResp = yield call(apiService.postProductPolicyRulevalidate, action.payload.args);
  if (response) {
    const isPass = ([] as Array<PolicyApplicantResp | PolicyInsuredResp | PolicyBeneficiaryResp>)
      .concat(response.policyApplicant, response.policyInsured, response.policyBeneficiary)
      .filter((result) => typeof result.isPass === 'boolean')
      .every((result) => result.isPass);
    if (isPass) {
      // 執行保單規則檢核 (洗錢名單)
      // 說明：透過檢核結果判斷是否為審核件並緩存 (若為審核件則不進入繳費流程)。
      const args: ProductPolicyRulevalidateReq = { ...action.payload.args, rulelist: [PolicyRulesEnum.LaunderingList] };
      yield put(validatePolicyRuleLaunderingListAction(args, action.payload.data));
    } else {
      // 保單規則檢核錯誤，顯示錯誤訊息
      const allErrors = ([] as Array<PolicyApplicantResp | PolicyInsuredResp | PolicyBeneficiaryResp>)
        .concat(response.policyApplicant, response.policyInsured, response.policyBeneficiary)
        .filter((result) => typeof result.isPass === 'boolean')
        .reduce((acc: ErrMsgResp[], curr) => acc.concat(curr.errMsg), []);
      const firstError = allErrors.find((item) => !item.isPass);
      if (firstError) modalService.base('系統提醒', firstError.msg);
    }
  }
}

/**
 * @description 執行保單規則檢核 (洗錢名單)
 */
function * validatePolicyRuleLaunderingList (action: ValidatePolicyRuleLaunderingListAction) {
  // 說明：透過檢核結果判斷是否為審核件並緩存 (若為審核件則不進入繳費流程)。
  const response: ProductPolicyRulevalidateResp = yield call(apiService.postProductPolicyRulevalidate, action.payload.args);
  if (response) {
    const isPass = ([] as Array<PolicyApplicantResp | PolicyInsuredResp | PolicyBeneficiaryResp>)
      .concat(response.policyApplicant, response.policyInsured, response.policyBeneficiary)
      .filter((result) => typeof result.isPass === 'boolean')
      .every((result) => result.isPass);
    const isAudit = ([] as Array<PolicyApplicantResp | PolicyInsuredResp | PolicyBeneficiaryResp>)
      .concat(response.policyApplicant, response.policyInsured, response.policyBeneficiary)
      .filter((result) => typeof result.isPass === 'boolean' && typeof result.isAudit === 'boolean')
      .some((result) => !result.isPass && result.isAudit);
    if (isPass || isAudit) {
      // 若為審核件則將狀態緩存至 Store
      if (isAudit) yield put(setPolicyStatusAction({ isAudit }));
      // 緩存表單資料
      yield put(saveInsuranceInfoPageDataAction(action.payload.data));
      // 將「填寫資料」(Step-2-2) 設為可訪問步驟
      yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__INSURANCE_INFO__CLAUSES]));
      // 導頁至「填寫資料」(Step-2-2)
      yield put(push(ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__INSURANCE_INFO__CLAUSES));
    } else {
      // 保單規則檢核錯誤，顯示錯誤訊息
      const allErrors = ([] as Array<PolicyApplicantResp | PolicyInsuredResp | PolicyBeneficiaryResp>)
        .concat(response.policyApplicant, response.policyInsured, response.policyBeneficiary)
        .filter((result) => typeof result.isPass === 'boolean')
        .reduce((acc: ErrMsgResp[], curr) => acc.concat(curr.errMsg), []);
      const firstError = allErrors.find((item) => !item.isPass);
      if (firstError) modalService.base('系統提醒', firstError.msg);
    }
  }
}

/**
 * @description 發送 OTP 驗證碼
 */
function * sendOTP (action: SendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 將「確認資料」(Step-2-1) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__CONFIRM_INFO__OTP_AUTH]));
    // 導頁至「確認資料」(Step-2-1)
    yield put(push(ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__CONFIRM_INFO__OTP_AUTH));
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP (action: ResendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 更新 OTP 資料
    yield put(resendOTPDoneAction(response));
  }
}

/**
 * @description 驗證 OTP 驗證碼
 */
function * authOTP (action: AuthOTPAction) {
  // 說明：若驗證 OTP 成功，則呼叫 (IR#014) 新增保單 API。
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.authOTPArgs);
  if (response) {
    // 送出保單資料 (新增保單)
    yield put(sendPolicyAction(action.payload.policyArgs));
  }
}

/**
 * @description 送出保單 (新增保單)
 */
function * sendPolicy (action: SendPolicyAction) {
  const response: PolicyTravelResp = yield call(apiService.postPolicyTravel, action.payload.args);
  if (response) {
    const policyStatusState: TravelInsuranceState['policyStatus'] = yield select((state: RootState) => state.features.insure.travelInsurance.policyStatus);
    if (policyStatusState?.isAudit) {
      // 說明：審核件，不進入繳費流程。
      const result: SweetAlertResult = yield modalService.base('系統提醒', '親愛的客戶您好：本公司已收到您的網路投保申請，目前正在進行人工審核，預計兩個工作日內將以簡訊或E-mail通知您審核結果，謝謝！', '回首頁');
      if (result) yield put(push(ROUTES.HOME__MAIN));
    } else {
      // 說明：非審核件 (一般案件) 進入繳費流程。
      const premiumTotal = action.payload.args.policyTravel.premTotal; // 總保費
      // 送出保單 (新增保單) 完成
      yield put(sendPolicyDoneAction(premiumTotal, response));
      // 導頁至「線上繳費」(Step-4-1)
      yield put(push(ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__PAYMENT));
    }
  }
}

/**
 * @description 執行信用卡繳費
 */
function * executeCreditCardPayment (action: ExecuteCreditCardPaymentAction) {
  const response: BaseResp<PaymentCreditAuthResp> = yield call(apiService.postPaymentCreditAuth, action.payload.args);
  if (response.respHeader.returnCode === ReturnCodesEnum.CodeSuccess) {
    // 將「完成投保」頁步驟 (Step-5) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__COMPLETE]));
    // 導頁至「完成投保」頁 (Step-5)
    yield put(push(ROUTES.ONLINE_INSURE__TRAVEL_INSURANCE__COMPLETE));
  } else {
    modalService.base('系統提醒', response.transResp.payment.responseMsg);
  }
}

export default function * watchTravelInsuranceSaga () {
  yield all([
    takeEvery(INSURE_TRAVEL_INSURANCE__INIT_PROCESS, sagaBoundary(initProcess)),
    takeEvery(INSURE_TRAVEL_INSURANCE__VALIDATE_POLICY_RULES, sagaBoundary(validatePolicyRules)),
    takeEvery(INSURE_TRAVEL_INSURANCE__VALIDATE_POLICY_RULE_LAUNDERING_LIST, sagaBoundary(validatePolicyRuleLaunderingList)),
    takeEvery(INSURE_TRAVEL_INSURANCE__SEND_OTP, sagaBoundary(sendOTP)),
    takeEvery(INSURE_TRAVEL_INSURANCE__RESEND_OTP, sagaBoundary(resendOTP)),
    takeEvery(INSURE_TRAVEL_INSURANCE__AUTH_OTP, sagaBoundary(authOTP)),
    takeEvery(INSURE_TRAVEL_INSURANCE__SEND_POLICY, sagaBoundary(sendPolicy)),
    takeEvery(INSURE_TRAVEL_INSURANCE__EXECUTE_CREDIT_CARD_PAYMENT, sagaBoundary(executeCreditCardPayment))
  ]);
}
